import React, { useState, useEffect } from "react";
import { Settings, Download, Trash2, AlertTriangle, Bell, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "@/lib/push";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function AccountMenu() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pw, setPw] = useState("");
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notifOn, setNotifOn] = useState(false);
  const [notifBusy, setNotifBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { isPushEnabled().then(setNotifOn); }, []);

  const toggleNotif = async () => {
    setNotifBusy(true);
    try {
      if (notifOn) { await disablePush(); setNotifOn(false); }
      else { await enablePush(); setNotifOn(true); toast.success(t("e5.notifEnabled")); }
    } catch (e) {
      if (e.message === "unsupported") toast.error(t("e5.notifUnsupported"));
      else if (e.message === "denied") toast.error(t("e5.notifBlocked"));
      else toast.error(apiError(e, t));
    } finally { setNotifBusy(false); }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "esvan-export.json"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e, t)); } finally { setExporting(false); }
  };

  const del = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete", { password: pw || null });
      toast.success(t("account.deleted"));
      await logout();
    } catch (e) { toast.error(apiError(e, t)); setDeleting(false); }
  };

  return (
    <>
      <button
        data-testid="account-menu-button"
        onClick={() => { setOpen(true); setConfirming(false); setPw(""); }}
        className="rounded-full p-2 text-[#4A6358] transition-colors hover:bg-[#F0F4F1] active:scale-95"
        aria-label={t("account.title")}
      >
        <Settings size={18} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="account-dialog" className="max-w-[360px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("account.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#7A9488]">{t("account.loggedInAs")}: <span className="font-semibold text-[#1A2E26]" dir="auto">{user?.email}</span></p>

          {!confirming ? (
            <div className="mt-2 space-y-2">
              <button data-testid="account-export-button" onClick={exportData} disabled={exporting}
                className="flex w-full items-center gap-2 rounded-xl border border-[#E2EAE5] bg-white px-3 py-3 text-sm font-semibold text-[#2D5A4C] active:scale-[0.99] disabled:opacity-50">
                <Download size={16} /> {exporting ? t("account.exporting") : t("account.export")}
              </button>
              <p className="px-1 text-[11px] text-[#B0C2B8]">{t("account.exportDesc")}</p>
              <button data-testid="account-subscription-link" onClick={() => navigate("/subscription")}
                className="flex w-full items-center gap-2 rounded-xl border border-[#E2EAE5] bg-white px-3 py-3 text-sm font-semibold text-[#2D5A4C] active:scale-[0.99]">
                <CreditCard size={16} /> {t("bill.title")}
              </button>
              <button data-testid="account-notifications-toggle" onClick={toggleNotif} disabled={notifBusy || !pushSupported()}
                className="flex w-full items-center gap-2 rounded-xl border border-[#E2EAE5] bg-white px-3 py-3 text-sm font-semibold text-[#2D5A4C] active:scale-[0.99] disabled:opacity-50">
                <Bell size={16} /> {notifOn ? t("e5.notifEnabled") : t("e5.notifEnable")}
              </button>
              <p className="px-1 text-[11px] text-[#B0C2B8]">{pushSupported() ? t("e5.notifDesc") : t("e5.notifUnsupported")}</p>
              <button data-testid="account-delete-button" onClick={() => setConfirming(true)}
                className="flex w-full items-center gap-2 rounded-xl border border-[#F3D0CB] bg-[#FDF2F0] px-3 py-3 text-sm font-semibold text-[#C03A2B] active:scale-[0.99]">
                <Trash2 size={16} /> {t("account.delete")}
              </button>
            </div>
          ) : (
            <div data-testid="account-delete-confirm" className="mt-2 space-y-3 rounded-xl border border-[#F3D0CB] bg-[#FDF2F0] p-3">
              <p className="flex items-start gap-1.5 text-xs font-semibold text-[#C03A2B]">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {t("account.deleteConfirm")}
              </p>
              <p className="text-[11px] text-[#8A5A52]">{t("account.deleteDesc").replace("{n}", "30")}</p>
              <input data-testid="account-delete-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder={t("account.password")} className="h-10 w-full rounded-xl border border-[#E2EAE5] px-3 text-sm" />
              <div className="flex gap-2">
                <button data-testid="account-delete-confirm-button" onClick={del} disabled={deleting}
                  className="rounded-full bg-[#C03A2B] px-4 py-2 text-xs font-semibold text-white active:scale-95 disabled:opacity-50">
                  {deleting ? "…" : t("account.delete")}
                </button>
                <button data-testid="account-delete-cancel" onClick={() => setConfirming(false)}
                  className="rounded-full border border-[#E2EAE5] px-4 py-2 text-xs font-semibold text-[#4A6358]">{t("account.cancel")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
